import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/ui/Header';
import Footer from './you-tube-video-downloader-landing-page/components/Footer';
import SplashCursor from './you-tube-video-downloader-landing-page/components/SplashCursor';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Icon from '../components/AppIcon';

const SupportPage = () => {
  const [activeTab, setActiveTab] = useState('faq');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactSubmit = (e) => {
    e?.preventDefault();
    alert('Thank you for your message! We will get back to you soon. (Demo mode)');
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  const faqData = [
    {
      question: 'How do I download a YouTube video?',
      answer: 'Simply copy the YouTube video URL, paste it into our download field, select your preferred quality, and click the download button. The video will be processed and downloaded to your device.'
    },
    {
      question: 'Is Zee Downloader free to use?',
      answer: 'Yes, Zee Downloader is completely free to use. There are no hidden fees, subscriptions, or registration requirements. You can download unlimited videos at no cost.'
    },
    {
      question: 'What video qualities are supported?',
      answer: 'We support all major video qualities including 4K (2160p), 1080p HD, 720p HD, 480p SD, and 360p. The available qualities depend on the original video upload quality.'
    },
    {
      question: 'Is it safe to use Zee Downloader?',
      answer: 'Absolutely! We use SSL encryption to protect your privacy and data. We do not store any personal information or downloaded content on our servers. Your downloads are completely anonymous.'
    },
    {
      question: 'Can I download videos on mobile devices?',
      answer: 'Yes, Zee Downloader is fully responsive and works perfectly on all devices including smartphones, tablets, and desktop computers. Our mobile interface is touch-optimized for the best experience.'
    },
    {
      question: 'Why is my download taking so long?',
      answer: 'Download speed depends on several factors including your internet connection, video file size, and server load. Higher quality videos (4K, 1080p) will take longer to download than lower quality versions.'
    },
    {
      question: 'Can I download playlists or multiple videos?',
      answer: 'Currently, we support individual video downloads. Playlist download functionality is being developed and will be available in a future update.'
    },
    {
      question: 'What should I do if a download fails?',
      answer: 'If a download fails, please check the video URL, ensure the video is public, and try again. If the problem persists, please contact our support team with the video URL and error details.'
    }
  ];

  const supportOptions = [
    {
      icon: 'MessageSquare',
      title: 'Live Chat',
      description: 'Get instant help from our support team',
      action: 'Start Chat',
      availability: 'Available 24/7'
    },
    {
      icon: 'Mail',
      title: 'Email Support',
      description: 'Send us a detailed message',
      action: 'Send Email',
      availability: 'Response within 24h'
    },
    {
      icon: 'FileText',
      title: 'Documentation',
      description: 'Browse our comprehensive guides',
      action: 'View Docs',
      availability: 'Always available'
    },
    {
      icon: 'Users',
      title: 'Community Forum',
      description: 'Connect with other users',
      action: 'Join Forum',
      availability: 'Active community'
    }
  ];

  const troubleshootingSteps = [
    {
      step: '1',
      title: 'Check Video URL',
      description: 'Ensure the YouTube video URL is correct and the video is publicly available.'
    },
    {
      step: '2',
      title: 'Clear Browser Cache',
      description: 'Clear your browser cache and cookies, then try downloading again.'
    },
    {
      step: '3',
      title: 'Try Different Quality',
      description: 'Some qualities might not be available. Try selecting a different video quality.'
    },
    {
      step: '4',
      title: 'Check Internet Connection',
      description: 'Ensure you have a stable internet connection for the download process.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Support & Help - Zee Downloader | Get Help</title>
        <meta name="description" content="Get help with Zee Downloader. Find answers to common questions, contact support, and troubleshoot issues." />
      </Helmet>
      <div className="min-h-screen bg-background relative overflow-hidden">
        <SplashCursor />
        
        <div className="fixed inset-0 bg-background/20 backdrop-blur-sm" style={{ zIndex: 2 }} />
        
        <div className="relative" style={{ zIndex: 50 }}>
          <Header />
        </div>

        <main className="relative pt-20 pb-8" style={{ zIndex: 10 }}>
          <div className="glass-container">
            {/* Page Header */}
            <div className="text-center py-12 mb-8">
              <div className="glass-card p-8 max-w-3xl mx-auto">
                <h1 className="text-fluid-4xl font-bold text-foreground mb-4">
                  Support & Help Center
                </h1>
                <p className="text-fluid-lg text-muted-foreground">
                  Need help? We're here to assist you with any questions or issues you might have.
                </p>
              </div>
            </div>

            {/* Support Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {supportOptions?.map((option, index) => (
                <div key={index} className="glass-card p-6 text-center micro-lift glass-hover">
                  <div className="glass-card p-4 w-fit mx-auto mb-4">
                    <Icon name={option?.icon} size={24} className="text-accent" />
                  </div>
                  <h3 className="text-fluid-lg font-semibold text-foreground mb-2">
                    {option?.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {option?.description}
                  </p>
                  <p className="text-xs text-accent mb-4">
                    {option?.availability}
                  </p>
                  <Button variant="ghost" size="sm" className="w-full">
                    {option?.action}
                  </Button>
                </div>
              ))}
            </div>

            {/* Tab Navigation */}
            <div className="glass-card p-2 mb-8 max-w-md mx-auto">
              <div className="flex space-x-1">
                {[
                  { id: 'faq', label: 'FAQ' },
                  { id: 'contact', label: 'Contact Us' },
                  { id: 'troubleshoot', label: 'Troubleshooting' }
                ]?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium smooth-transition ${
                      activeTab === tab?.id 
                        ? 'bg-accent text-accent-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:glass-light'
                    }`}
                  >
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'faq' && (
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-fluid-3xl font-bold text-foreground mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-fluid-base text-muted-foreground">
                    Find quick answers to the most common questions about Zee Downloader.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {faqData?.map((faq, index) => (
                    <div key={index} className="glass-card p-6">
                      <h3 className="text-fluid-lg font-semibold text-foreground mb-3">
                        {faq?.question}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {faq?.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-fluid-3xl font-bold text-foreground mb-4">
                    Contact Us
                  </h2>
                  <p className="text-fluid-base text-muted-foreground">
                    Can't find what you're looking for? Send us a message and we'll help you out.
                  </p>
                </div>
                
                <div className="glass-card p-8">
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Name
                        </label>
                        <Input
                          type="text"
                          required
                          value={contactForm?.name}
                          onChange={(e) => setContactForm({...contactForm, name: e?.target?.value})}
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Email
                        </label>
                        <Input
                          type="email"
                          required
                          value={contactForm?.email}
                          onChange={(e) => setContactForm({...contactForm, email: e?.target?.value})}
                          placeholder="zee@gmail.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Subject
                      </label>
                      <Input
                        type="text"
                        required
                        value={contactForm?.subject}
                        onChange={(e) => setContactForm({...contactForm, subject: e?.target?.value})}
                        placeholder="What is this about?"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Message
                      </label>
                      <textarea
                        required
                        rows={6}
                        value={contactForm?.message}
                        onChange={(e) => setContactForm({...contactForm, message: e?.target?.value})}
                        placeholder="Please describe your issue or question in detail..."
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring smooth-transition resize-none"
                      />
                    </div>
                    
                    <Button type="submit" size="lg" className="w-full" iconName="Send">
                      Send Message
                    </Button>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'troubleshoot' && (
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-fluid-3xl font-bold text-foreground mb-4">
                    Troubleshooting Guide
                  </h2>
                  <p className="text-fluid-base text-muted-foreground">
                    Follow these steps to resolve common issues with downloading videos.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {troubleshootingSteps?.map((step, index) => (
                    <div key={index} className="glass-card p-6">
                      <div className="flex items-start space-x-4">
                        <div className="glass-card p-3 text-accent font-bold text-lg">
                          {step?.step}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-fluid-lg font-semibold text-foreground mb-2">
                            {step?.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed">
                            {step?.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="glass-card p-6 text-center">
                  <Icon name="HelpCircle" size={32} className="text-accent mx-auto mb-4" />
                  <h3 className="text-fluid-xl font-semibold text-foreground mb-2">
                    Still having issues?
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    If these steps don't resolve your problem, please contact our support team.
                  </p>
                  <Button onClick={() => setActiveTab('contact')} iconName="MessageSquare">
                    Contact Support
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>

        <div className="relative" style={{ zIndex: 10 }}>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default SupportPage;